import { Injectable, computed, signal } from '@angular/core';

import type { AuthStatus, AuthUser, SessionEndReason } from './auth.models';

@Injectable({ providedIn: 'root' })
export class AuthStore {
  private readonly userSignal = signal<AuthUser | null>(null);
  private readonly statusSignal = signal<AuthStatus>('initializing');
  private readonly lastEndReasonSignal = signal<SessionEndReason | null>(null);

  readonly user = this.userSignal.asReadonly();
  readonly status = this.statusSignal.asReadonly();

  readonly lastEndReason = this.lastEndReasonSignal.asReadonly();

  readonly isAuthenticated = computed(() => this.statusSignal() === 'authenticated');
  readonly isInitializing = computed(() => this.statusSignal() === 'initializing');
  readonly displayName = computed(() => this.userSignal()?.displayName ?? '');

  readonly selectedCompany = computed(() => {
    const user = this.userSignal();

    if (user === null) {
      return null;
    }

    return user.companies.find((company) => company.id === user.selectedCompanyId) ?? null;
  });

  setUser(user: AuthUser): void {
    this.userSignal.set(user);
    this.statusSignal.set('authenticated');
    this.lastEndReasonSignal.set(null);
  }

  clear(reason: SessionEndReason | null = null): void {
    this.userSignal.set(null);
    this.statusSignal.set('anonymous');
    this.lastEndReasonSignal.set(reason);
  }

  acknowledgeEndReason(): void {
    this.lastEndReasonSignal.set(null);
  }
}
